import { useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import {
  useUpdate,
  useNotify,
  SimpleForm,
  Edit,
  useResourceContext,
  required,
  maxLength,
  useRefresh,
  minLength,
  TextInput,
  useTranslate,
  ReferenceInput,
  AutocompleteInput,
} from "react-admin";
import MuiDatePickerInput from "../../common/MuiDatePickerInput";
import SaveDeleteBtns from "../../saveDeleteBtns/SaveDeleteBtn";
import currentDate from "../../../utils/currentDate";
import JournalItemForm from "./components/JournalItemForm";
import CodeNumberInput from "../../common/CodeNumberInput";
import {
  filterToQuery,
  inputText,
  matchSuggestion,
  OptionRenderer,
} from "../../customerDebtPayments/common/CustomSelectOption";

// const equalOrGreaterThanPaymentItem = (value, allValues, elm) => {
//   let count = 0;
//   for (let i = 0; i < allValues.customer_debt_payment_items.length; i++) {
//     const elm = allValues.customer_debt_payment_items[i];
//     count += elm.amount;
//   }
//   if (count > value) {
//     return (
//       "Not enough amount to accommodate all item's payment (" +
//       toCurrency(true).format(count) +
//       ")"
//     );
//   }
//   return undefined;
// };

const validateCategory = [required(), minLength(36), maxLength(36)];
const validateNumber = [required(), minLength(14), maxLength(14)];
const validateDate = [required(), minLength(10), maxLength(10)];
// const validateSalesman = [minLength(36), maxLength(36)];
// const validatePrice = [required(), number(), equalOrGreaterThanPaymentItem];
const validateNote = [required()];

export default function JournalEditDialog({ open, setOpen, recordId }) {
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const resource = useResourceContext(); // cuman nama resource
  const t = useTranslate();

  const save = useCallback(
    async (values) => {
      try {
        await update(
          resource,
          { id: values.id, data: values },
          {
            returnPromise: true,
            onSuccess: (data) => {
              notify("ra.notification.updated", {
                messageArgs: { smart_count: 1 },
              });
              setOpen(false);
              refresh();
            },
          }
        );
      } catch (error) {
        if (error.body && error.body.errors) {
          if (error.body.errors === "403") {
            // munculkan notifikasi erro ketika sudah approved
            notify("mine.notification.403_error", { type: "error" });
          } else {
            // The shape of the returned validation errors must match the shape of the form
            return error.body.errors;
          }
        }
      }
    },
    [update]
  );

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      keepMounted={false}
      fullWidth={true}
      maxWidth="lg"
    >
      <Edit
        title={" "}
        id={recordId}
        actions={false}
        sx={{ "& .RaEdit-main": { mt: 0 } }}
      >
        <SimpleForm
          warnWhenUnsavedChanges
          onSubmit={save}
          toolbar={<SaveDeleteBtns callback={handleClose} />}
          mode="onBlur"
          reValidateMode="onBlur"
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 0,
            }}
          >
            <DialogTitle>{t("mine.dialog.edit_journal")}</DialogTitle>
            <IconButton onClick={handleClose} sx={{ mr: "11px" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent sx={{ alignSelf: "normal" }}>
            <TextInput source="id" sx={{ display: "none" }} />

            <CodeNumberInput
              // open={open}
              dependOn="date"
              variant="outlined"
              source="code"
              label="mine.label.receipt_no"
              fullWidth
              validate={validateNumber}
              disabled
            />
            <MuiDatePickerInput
              variant="outlined"
              source="date"
              fullWidth
              isRequired
              validate={validateDate}
              defaultValue={currentDate()}
            />
            <ReferenceInput
              source="category_id"
              reference="categories"
              perPage={20}
              sort={{ field: "name", order: "ASC" }}
            >
              <AutocompleteInput
                label="mine.label.category"
                variant="outlined"
                fullWidth
                validate={validateCategory}
                optionValue="id"
                optionText="name"
                filterToQuery={(searchText) => ({ name: searchText })}
              />
            </ReferenceInput>
            <ReferenceInput
              source="salesman_id"
              reference="salesmen"
              perPage={20}
              sort={{ field: "salesmen.code", order: "ASC" }}
              filter={{ "salesmen.active": "1" }}
              // enableGetChoices={(filters) => !!filters.all}
            >
              <AutocompleteInput
                variant="outlined"
                fullWidth
                optionValue="id"
                optionText={<OptionRenderer />}
                inputText={inputText}
                matchSuggestion={matchSuggestion}
                filterToQuery={filterToQuery}
                parse={(value) => value || null}
              />
            </ReferenceInput>
            <TextInput
              variant="outlined"
              source="note"
              label="mine.label.note"
              fullWidth
              validate={validateNote}
              multiline={true}
              rows={4}
            />
            <Box
                sx={{
                  maxHeight: '300px', // Sesuaikan tinggi maksimum sesuai kebutuhan Anda
                  overflowY: 'auto',
                  border: '1px solid #ccc', // Opsional: untuk visualisasi batas
                  borderRadius: '4px',
                  marginBottom: '24px',
                  p: 1, // Opsional: padding
                }}
              >
            <JournalItemForm />
            </Box>
          </DialogContent>
        </SimpleForm>
      </Edit>
    </Dialog>
  );
}
